// client/src/pages/UserLogin/styles.js
import styled, { keyframes } from 'styled-components';

// ========== Animations ==========
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ========== Layout ==========
export const LoginContainer = styled.div`
  min-height: 100vh;
  background: #e1e1e1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  direction: rtl;
`;

export const LoginForm = styled.form`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

export const Title = styled.h1`
  text-align: center;
  color: #333;
  margin: 0 0 8px;
  font-size: 1.8rem;
  font-weight: 600;
`;

export const Subtitle = styled.p`
  margin: 0 0 32px;
  color: #666;
  font-size: 1rem;
  text-align: center;
`;

// ========== Form Elements ==========
export const InputGroup = styled.div`
  margin-bottom: 20px;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 600;
  font-size: 0.9rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
  color: #333;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: white;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

// ========== Button ==========
export const Button = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: ${(props) =>
    props.disabled
      ? '#ccc'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// ========== Messages ==========
export const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  padding: 14px 18px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #fecaca;
  text-align: center;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const SuccessMessage = styled.div`
  background: #d1fae5;
  color: #065f46;
  padding: 14px 18px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #a7f3d0;
  text-align: center;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  animation: ${fadeIn} 0.3s ease-out;
`;

// ========== Links ==========
export const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  text-align: center;
`;

export const LinkText = styled.span`
  color: ${(props) => (props.secondary ? '#999' : '#667eea')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  font-size: ${(props) => (props.small ? '0.85rem' : '0.9rem')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: all 0.2s;

  &:hover:not([disabled]) {
    color: ${(props) => (props.secondary ? '#666' : '#764ba2')};
    text-decoration: underline;
  }
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 24px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e9ecef;
  }

  span {
    color: #999;
    font-size: 0.85rem;
  }
`;

// ========== Loading State ==========
export const LoadingContainer = styled.div`
  background: white;
  padding: 60px 40px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

export const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto 16px;
`;

export const LoadingText = styled.p`
  color: #666;
  font-size: 1rem;
  margin: 0;
`;
